import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import { api } from '../services/api';
import { formatVendorCurrency } from '../utils/currency';

interface CaseEstimate {
  id: number;
  vendor_user_id: number;
  vendor_firm_id: number;
  billing_type: string;
  rate: number;
  quantity: number;
  total_amount: number;
  description: string;
  status: 'draft' | 'pending' | 'approved' | 'rejected';
  approved_by: number | null;
  approved_at: string | null;
  rejection_reason: string | null;
  is_invoiced: boolean;
  created_at: string;
  vendorUser: {
    id: number;
    name: string;
    email: string;
    currency?: string | null;
  };
  vendorFirm: {
    id: number;
    name: string;
  };
  approvedBy?: {
    id: number;
    name: string;
  };
}

interface User {
  id: number;
  name: string;
  email: string;
  roles?: Array<{
    id: number;
    name: string;
    slug: string;
  }>;
}

interface CaseEstimationProps {
  caseId: number;
  projectId: number;
  user: User | null;
  isVendorStaff: boolean;
}

const CaseEstimation: React.FC<CaseEstimationProps> = ({ 
  caseId, 
  projectId, 
  user, 
  isVendorStaff 
}) => {
  const [estimates, setEstimates] = useState<CaseEstimate[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editingEstimate, setEditingEstimate] = useState<CaseEstimate | null>(null);
  
  const [formData, setFormData] = useState({
    billing_type: '',
    quantity: '',
    description: '',
    rate: '',
  });

  // Role checking functions
  const isPmpAdmin = () => {
    if (!user?.roles) return false;
    return user.roles.some((role) => role.slug === 'pmp_admin');
  };

  const isSiteAdmin = () => {
    if (!user?.roles) return false;
    return user.roles.some((role) => role.slug === 'site_admin');
  };

  const isPmpAdminOrSiteAdmin = () => {
    return isPmpAdmin() || isSiteAdmin();
  };

  const isVendorUser = () => {
    if (!user?.roles) return false;
    const vendorRoleSlugs = ['vendor_staff', 'vendor_admin'];
    return user.roles.some((role) => vendorRoleSlugs.includes(role.slug));
  };

  useEffect(() => {
    loadEstimates();
  }, [caseId]);

  const loadEstimates = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/cases/${caseId}/estimates`);
      if (response.data.success) {
        setEstimates(response.data.data);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load estimates');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.quantity) {
      setError('Please fill in quantity');
      return;
    }
    
    // For PMP admins and Site admins, validate billing type and rate if provided
    if (isPmpAdminOrSiteAdmin()) {
      if (!formData.billing_type) {
        setError('Please select a billing type');
        return;
      }
      if (formData.rate && parseFloat(formData.rate) < 0) {
        setError('Rate must be a positive number');
        return;
      }
    }

    try {
      setSubmitting(true);
      const payload: any = {
        quantity: parseInt(formData.quantity),
        description: formData.description,
      };
      
      // Only include billing_type and rate for PMP admins and Site admins
      if (isPmpAdminOrSiteAdmin()) {
        payload.billing_type = formData.billing_type;
        if (formData.rate) {
          payload.rate = parseFloat(formData.rate);
        }
      }
      
      const response = await api.post(`/cases/${caseId}/estimates`, payload);

      if (response.data.success) {
        setSuccess('Estimate saved as draft successfully');
        setFormData({ billing_type: '', quantity: '', description: '', rate: '' });
        loadEstimates();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save estimate');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditDraft = (estimate: CaseEstimate) => {
    setEditingEstimate(estimate);
    setFormData({
      billing_type: estimate.billing_type,
      quantity: estimate.quantity.toString(),
      description: estimate.description || '',
      rate: estimate.rate.toString(),
    });
  };

  const handleUpdateDraft = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editingEstimate || !formData.quantity) {
      setError('Please fill in quantity');
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        quantity: parseInt(formData.quantity),
        description: formData.description,
      };
      
      const response = await api.put(`/estimates/${editingEstimate.id}/draft`, payload);

      if (response.data.success) {
        setSuccess('Draft estimate updated successfully');
        setEditingEstimate(null);
        setFormData({ billing_type: '', quantity: '', description: '', rate: '' });
        loadEstimates();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update draft estimate');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitForReview = async (estimate: CaseEstimate) => {
    try {
      setSubmitting(true);
      const response = await api.post(`/estimates/${estimate.id}/submit-for-review`);

      if (response.data.success) {
        setSuccess('Estimate submitted for review successfully');
        loadEstimates();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to submit estimate for review');
    } finally {
      setSubmitting(false);
    }
  };

  const cancelEdit = () => {
    setEditingEstimate(null);
    setFormData({ billing_type: '', quantity: '', description: '', rate: '' });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'pending': return 'warning';
      case 'draft': return 'info';
      default: return 'default';
    }
  };

  const getBillingTypeLabel = (type: string) => {
    switch (type) {
      case 'page_based': return 'Page Based';
      case 'case_based': return 'Case Based';
      case 'time_based': return 'Time Based';
      default: return type;
    }
  };

  const getQuantityLabel = (type: string) => {
    switch (type) {
      case 'page_based': return 'Pages';
      case 'case_based': return 'Cases';
      case 'time_based': return 'Hours';
      default: return 'Quantity';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Estimation Form for Vendor Staff and PMP Admins */}
      {(isVendorStaff || isPmpAdminOrSiteAdmin()) && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {editingEstimate ? 'Edit Draft Estimate' : 'Submit Case Estimate'}
          </Typography>
          <form onSubmit={editingEstimate ? handleUpdateDraft : handleSubmit}>
            {/* Show billing type and rate fields only for PMP admins/Site admins or when editing */}
            {(isPmpAdminOrSiteAdmin() || editingEstimate) && (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
                <FormControl fullWidth required>
                  <InputLabel>Billing Type</InputLabel>
                  <Select
                    value={formData.billing_type}
                    onChange={(e) => setFormData({ ...formData, billing_type: e.target.value })}
                    label="Billing Type"
                    disabled={!!editingEstimate && !isPmpAdmin()}
                  >
                    <MenuItem value="page_based">Page Based</MenuItem>
                    <MenuItem value="case_based">Case Based</MenuItem>
                    <MenuItem value="time_based">Time Based</MenuItem>
                    <MenuItem value="flat_rate">Flat Rate</MenuItem>
                  </Select>
                </FormControl>
                
                <TextField
                  fullWidth
                  label="Rate per unit (Optional)"
                  type="number"
                  value={formData.rate}
                  onChange={(e) => setFormData({ ...formData, rate: e.target.value })}
                  inputProps={{ min: 0, step: 0.01 }}
                  helperText="Leave empty to use default rate or set a custom rate"
                  disabled={!!editingEstimate && !isPmpAdmin()}
                />
              </Box>
            )}
            
            {/* Quantity field for all users */}
            <TextField
              fullWidth
              required
              label={isPmpAdminOrSiteAdmin() && formData.billing_type ? getQuantityLabel(formData.billing_type) : 'Quantity'}
              type="number"
              value={formData.quantity}
              onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
              inputProps={{ min: 1 }}
              sx={{ mb: 2 }}
            />
            
            {/* Info message for vendor staff */}
            {isVendorStaff && !editingEstimate && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Billing type and rate will be automatically set from your project rates.
              </Alert>
            )}
            
            {/* Info message when editing */}
            {editingEstimate && (
              <Alert severity="info" sx={{ mb: 2 }}>
                You can only edit quantity and description. Billing type and rate are fixed.
              </Alert>
            )}
            
            <TextField
              fullWidth
              label="Description (Optional)"
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              sx={{ mb: 2 }}
            />
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                type="submit"
                variant="contained"
                disabled={submitting || (isPmpAdminOrSiteAdmin() && !formData.billing_type) || !formData.quantity}
              >
                {submitting ? <CircularProgress size={20} /> : 
                 editingEstimate ? 'Update Draft' : 'Save as Draft'}
              </Button>
              
              {editingEstimate && (
                <Button
                  variant="outlined"
                  onClick={cancelEdit}
                  disabled={submitting}
                >
                  Cancel
                </Button>
              )}
            </Box>
          </form>
        </Paper>
      )}

      {/* Estimates History */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Estimate History
        </Typography>
        
        {loading ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress />
          </Box>
        ) : estimates.length === 0 ? (
          <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
            No estimates submitted yet
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Vendor</TableCell>
                  <TableCell>Billing Type</TableCell>
                  <TableCell>Quantity</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Rate</TableCell>
                  <TableCell>Total Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Submitted</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {estimates.map((estimate) => (
                  <TableRow key={estimate.id}>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {estimate.vendorUser?.name || 'Unknown User'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {estimate.vendorFirm?.name || 'Unknown Firm'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{getBillingTypeLabel(estimate.billing_type)}</TableCell>
                    <TableCell>
                      {estimate.quantity} {getQuantityLabel(estimate.billing_type)}
                    </TableCell>
                    <TableCell>
                      {estimate.description || '—'}
                    </TableCell>
                    <TableCell>
                      {formatVendorCurrency(
                        estimate.rate,
                        estimate.vendorUser?.currency
                      )}
                    </TableCell>
                    <TableCell>
                      {formatVendorCurrency(
                        estimate.total_amount,
                        estimate.vendorUser?.currency
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={estimate.status.toUpperCase()}
                        color={getStatusColor(estimate.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(estimate.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <EstimateActions 
                        estimate={estimate} 
                        onUpdate={loadEstimates}
                        onEdit={handleEditDraft}
                        onSubmitForReview={handleSubmitForReview}
                        isVendorUser={isVendorUser()}
                        isPmpAdmin={isPmpAdminOrSiteAdmin()}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccess(null)} severity="success">
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// Estimate Actions Component
interface EstimateActionsProps {
  estimate: CaseEstimate;
  onUpdate: () => void;
  onEdit: (estimate: CaseEstimate) => void;
  onSubmitForReview: (estimate: CaseEstimate) => void;
  isVendorUser: boolean;
  isPmpAdmin: boolean;
}

const EstimateActions: React.FC<EstimateActionsProps> = ({ 
  estimate, 
  onUpdate, 
  onEdit, 
  onSubmitForReview,
  isVendorUser,
  isPmpAdmin 
}) => {
  const [action, setAction] = useState<'approve' | 'reject' | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleAction = async () => {
    if (!action) return;

    try {
      setSubmitting(true);
      const payload = action === 'approve' 
        ? { action: 'approve' }
        : { action: 'reject', rejection_reason: rejectionReason };

      const response = await api.put(`/estimates/${estimate.id}`, payload);
      
      if (response.data.success) {
        onUpdate();
      }
    } catch (err: any) {
      console.error('Failed to update estimate:', err);
    } finally {
      setSubmitting(false);
      setAction(null);
      setRejectionReason('');
    }
  };

  // Show different actions based on status and user role
  if (estimate.status === 'draft' && isVendorUser) {
    return (
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          size="small"
          variant="outlined"
          onClick={() => onEdit(estimate)}
        >
          Edit
        </Button>
        <Button
          size="small"
          variant="contained"
          color="primary"
          onClick={() => onSubmitForReview(estimate)}
        >
          Submit for Review
        </Button>
      </Box>
    );
  }

  if (estimate.status === 'pending' && isPmpAdmin) {
    return (
      <Box>
        {action === null ? (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              variant="contained"
              color="success"
              onClick={() => setAction('approve')}
            >
              Approve
            </Button>
            <Button
              size="small"
              variant="contained"
              color="error"
              onClick={() => setAction('reject')}
            >
              Reject
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {action === 'reject' && (
              <TextField
                size="small"
                label="Rejection Reason"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                multiline
                rows={2}
              />
            )}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant="contained"
                onClick={handleAction}
                disabled={submitting || (action === 'reject' && !rejectionReason)}
              >
                {submitting ? <CircularProgress size={16} /> : 'Confirm'}
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setAction(null);
                  setRejectionReason('');
                }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    );
  }

  // No actions for other statuses or users
  return <Typography variant="body2" color="text.secondary">—</Typography>;
};

export default CaseEstimation;
